import { useCallback, useRef, useState, type DragEvent } from "react";
import { UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/shared/i18n";

interface Props {
  onFile: (file: File) => void;
  busy?: boolean;
}

export function PsdDropzone({ onFile, busy }: Props) {
  const { t } = useTranslation("psdTemplates");
  const inputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = Array.from(files).find((f) => /\.psd$/i.test(f.name));
      if (file) onFile(file);
    },
    [onFile],
  );

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHover(false);
    if (busy) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!busy) setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      className={cn(
        "border-2 border-dashed rounded-2xl p-10 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors text-center",
        hover ? "border-primary bg-primary/5" : "border-border bg-muted/30",
        busy && "opacity-60 cursor-not-allowed",
      )}
    >
      <UploadCloud className="w-12 h-12 text-muted-foreground" />
      <div className="space-y-1">
        <p className="text-base font-medium text-foreground">
          {busy ? t("dropzone.parsing") : t("dropzone.title")}
        </p>
        <p className="text-sm text-muted-foreground">{t("dropzone.subtitle")}</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".psd,image/vnd.adobe.photoshop"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        disabled={busy}
      />
    </div>
  );
}
